import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CTA, PageHero } from '../../components/Common';
import { CancelDeletionForm } from '../../components/CancelDeletionForm';
import { DemoForm } from '../../components/DemoForm';
import { PasswordResetForm } from '../../components/PasswordResetForm';
import { TrialSignupForm } from '../../components/TrialSignupForm';
import { Seo } from '../../components/Seo';
import { track } from '../../components/analytics';
import { businessDetails, faqs, faqTopics, onboardingMigration, pricing } from '../../data/site';
import { Head } from './sections';

export function Demo() {
  return (
    <>
      <Seo title="Request a demo" description="Request a tailored walkthrough of Transport Book." />
      <PageHero
        eyebrow="Talk to us"
        title="See Transport Book around your workflow."
        description="Tell us about your operation and the areas you want to improve."
      />
      <section className="section">
        <div className="container grid two">
          <div>
            <Head
              eyebrow="What to expect"
              title="A practical conversation, not a generic sales deck."
              body="We will focus on the trip, workshop, finance, reporting, GPS, or mobile workflows that matter to your team. The published plans are on the pricing page; fleets above 50 trucks are quoted for the intended setup."
            />
            <aside className="migration-note">
              <div className="eyebrow">Included in onboarding</div>
              <h2>{onboardingMigration.title}</h2>
              <p>{onboardingMigration.description}</p>
            </aside>
          </div>
          <DemoForm />
        </div>
      </section>
    </>
  );
}

export function Trial() {
  return (
    <>
      <Seo
        title="Start a free trial"
        description={`${pricing.freeTrial}: set up your organisation on Transport Book in a few minutes, with no approval needed.`}
      />
      <PageHero
        eyebrow={pricing.freeTrial}
        title="Start your own Transport Book trial."
        description="Tell us about your business, verify your mobile number, and your organisation is ready to sign in to straight away."
      />
      <section className="section">
        <div className="container grid two trial-layout">
          <div className="trial-aside">
            <Head
              eyebrow="How it works"
              title="Your organisation, set up while you wait."
              body={`Fill in your business details and choose the username and password you will sign in with. We verify your mobile number by SMS, then create your organisation at once — nobody needs to approve it. Your trial runs for ${pricing.freeTrialDays} days on the desktop app, in the browser and on Android.`}
            />
            <aside className="migration-note">
              <div className="eyebrow">Prefer a walkthrough?</div>
              <h2>We can set it up with you.</h2>
              <p>
                If you would rather see Transport Book around your own workflow first,{' '}
                <Link to="/request-demo">request a demo</Link>.
              </p>
            </aside>
          </div>
          <TrialSignupForm />
        </div>
      </section>
    </>
  );
}

export function ResetPassword() {
  return (
    <>
      <Seo
        title="Reset your password"
        description="Forgot your Transport Book password? Verify the mobile number on your account by SMS and choose a new one."
      />
      <PageHero
        eyebrow="Account"
        title="Reset your password."
        description="Enter your username and the mobile number on your account. We send a one-time code by SMS; type it in and choose a new password. The same password then works in the browser, on the desktop and on your phone."
      />
      <section className="section">
        <div className="container grid two">
          <div>
            <Head
              eyebrow="How it works"
              title="Your phone is the key."
              body="Every account that started from a free trial carries the mobile number it was verified with. A code sent to that number proves it is you, and the new password takes effect at once — every device still signed in with the old one is signed out."
            />
            <aside className="migration-note">
              <div className="eyebrow">No mobile number on your account?</div>
              <h2>Ask your administrator.</h2>
              <p>
                Staff accounts the office created by hand may have no verified number. Your
                organisation's administrator can set a new password for you from the staff accounts
                screen in Transport Book. If you are the administrator and cannot get in,{' '}
                <Link to="/request-demo">get in touch</Link> and we will verify you another way.
              </p>
            </aside>
          </div>
          <PasswordResetForm />
        </div>
      </section>
    </>
  );
}

function FaqItem({ faq, open }: { faq: { question: string; answer: string }; open?: boolean }) {
  return (
    <details
      className="faq-item"
      open={open}
      onToggle={event => {
        if (event.currentTarget.open) track('faq_open', { question: faq.question });
      }}
    >
      <summary>
        {faq.question}
        <span aria-hidden="true">+</span>
      </summary>
      <p>{faq.answer}</p>
    </details>
  );
}

export function Support() {
  const [query, setQuery] = useState('');
  const searchTerms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const searchAliases: Record<string, string> = {
    offline: 'connection',
    installation: 'install',
    credentials: 'access',
    login: 'credentials',
    web: 'browser',
    online: 'browser',
  };
  const filteredFaqs = faqs.filter(({ question, answer }) => {
    const text = `${question} ${answer}`.toLowerCase();
    return searchTerms.every(
      term => text.includes(term) || (searchAliases[term] && text.includes(searchAliases[term])),
    );
  });
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(({ question, answer }) => ({
      '@type': 'Question',
      name: question,
      acceptedAnswer: { '@type': 'Answer', text: answer },
    })),
  };
  return (
    <>
      <Seo
        title="Support"
        description="Installation, updates, offline behaviour, data storage, access, and troubleshooting guidance."
        schema={schema}
      />
      <PageHero
        eyebrow="Support"
        title="How can we help?"
        description="Answers for the Microsoft Store, the web app, APK installation, desktop installers, updates, offline use, and accounts."
      />
      <section className="section">
        <div className="container prose">
          <div className="faq-search">
            <label htmlFor="faq-query">What can we help you find?</label>
            <div className="faq-search-row">
              <input
                id="faq-query"
                type="search"
                value={query}
                onChange={event => setQuery(event.target.value)}
                placeholder="Search installation, updates, offline use…"
              />
              {query && (
                <button type="button" className="button secondary" onClick={() => setQuery('')}>
                  Clear
                </button>
              )}
            </div>
            <p role="status">
              {filteredFaqs.length} {filteredFaqs.length === 1 ? 'answer' : 'answers'}
              {query.trim() ? ' found' : ' to common questions'}
            </p>
          </div>
          {query.trim() ? (
            <div className="faq-list">
              {filteredFaqs.map(faq => (
                <FaqItem faq={faq} key={faq.question} open />
              ))}
            </div>
          ) : (
            faqTopics.map(topic => (
              <div className="faq-topic" key={topic.title}>
                <h2>{topic.title}</h2>
                <div className="faq-list">
                  {topic.questions.map(question => {
                    const faq = faqs.find(item => item.question === question);
                    return faq ? <FaqItem faq={faq} key={question} /> : null;
                  })}
                </div>
              </div>
            ))
          )}
          {filteredFaqs.length === 0 && (
            <p className="faq-empty">
              No matching answers. Try a shorter search, or{' '}
              <a href={`mailto:${businessDetails.emails.support}`}>email us</a>.
            </p>
          )}
        </div>
      </section>
      <CTA
        actions={
          <>
            <a className="button primary" href={`mailto:${businessDetails.emails.support}`}>
              Email {businessDetails.emails.support}
            </a>
            <Link className="button on-dark" to="/reset-password">
              Reset a password
            </Link>
          </>
        }
        body="Tell us what you are seeing, with a screenshot if you can, and we will help you get going."
        title="Still stuck?"
      />
    </>
  );
}

export function CancelDeletion() {
  return (
    <>
      <Seo
        noindex
        title="Cancel a scheduled deletion"
        description="An administrator can cancel the scheduled deletion of a Transport Book organisation before the data is removed."
      />
      <PageHero
        eyebrow="Account"
        title="Cancel a scheduled deletion."
        description="When an administrator asks to delete an organisation, everyone is signed out and the data is removed after 30 days. Until then, an administrator can cancel here with their own username and password."
      />
      <section className="section">
        <div className="container grid two">
          <div>
            <Head
              eyebrow="Before the date"
              title="Nothing is removed until then."
              body="Cancelling keeps the organisation and all its data exactly as it was, and everyone can sign in again. Every administrator is told by email when the deletion is scheduled, a week before it happens and when it is cancelled. Once the date passes, the data is removed and cannot be brought back."
            />
          </div>
          <CancelDeletionForm />
        </div>
      </section>
    </>
  );
}
